import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Business, Service, User, BusinessBranding, RecurrenceMode, RecurringPreviewResponse } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import UserProfileDropdown from '../components/UserProfileDropdown';
import BusinessHeader from '../components/BusinessHeader';
import BusinessFooter from '../components/BusinessFooter';
import BookingStep1 from '../components/booking/BookingStep1';
import BookingStep2 from '../components/booking/BookingStep2';
import BookingStep3 from '../components/booking/BookingStep3';
import BookingStep4 from '../components/booking/BookingStep4';
import BookingStep4ServiceAddress, { ServiceAddress as StepServiceAddress } from '../components/booking/BookingStep4ServiceAddress';
import BookingStepQuote, { QuoteRequestEntry, QUOTE_REQUEST_ALL_KEY } from '../components/booking/BookingStepQuote';
import BookingStepQuoteReview from '../components/booking/BookingStepQuoteReview';
import BookingPhoneRequiredModal from '../components/booking/BookingPhoneRequiredModal';
import TenantAskAiWidget from '../components/TenantAskAiWidget';
import CustomAlertModal from '../components/CustomAlertModal';
import { userHasPhoneRecord } from '../utils/phoneFormatUtils';
import { getThemeStyles } from '../utils/themeUtils';
import { getThemeColors as getLayoutThemeColors } from '../utils/themeSchemes';
import { Loader2, ChevronLeft, ChevronRight, Check, MessageSquare, ArrowLeft } from 'lucide-react';
import { formatLocalYmd, getBrowserTimeZone } from '../utils/timezoneUtils';
import { filterCustomerBookableServices } from '../utils/serviceUtils';
import { DEFAULT_BOOKING_SLOT_MINUTES, resolveBookingSlotMinutes } from '../utils/bookingSlotSettings';
import { DEFAULT_REGULAR_DEPOSIT_DUE_VALUE, formatDepositDueDurationLabel, normalizeDepositDueWithinUnit } from '../utils/depositDueDuration';

interface BookingData {
  selectedServices: Service[];
  selectedProfessionals: { [serviceId: string]: User | undefined };
  sameProfessionalForAll: boolean;
  selectedDate: Date | null;
  selectedTime: string;
  totalPrice: number;
  totalDuration: number;
  insistOnProfessional: { [serviceId: string]: boolean };
  notes?: string;
  serviceAddress?: StepServiceAddress;
  rememberServiceAddress?: boolean;
  quoteRequests?: { [serviceId: string]: QuoteRequestEntry };
  preferredScheduleText?: string;
}

const BusinessBookingPage: React.FC = () => {
  const { businessShortName } = useParams<{ businessShortName: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, updateUser, loading: authLoading } = useAuth();
  
  // Get rebook parameters early to initialize step
  const searchParams = new URLSearchParams(location.search);
  const isRebook = searchParams.get('rebook') === 'true';
  const rebookStep = searchParams.get('step');
  
  // Initialize step from URL if rebook is active (step 2 = professional selection; step 3 kept for backward compat)
  const initialStep = isRebook && (rebookStep === '2' || rebookStep === '3') ? 2 : 1;
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [business, setBusiness] = useState<Business | null>(null);
  const [branding, setBranding] = useState<BusinessBranding | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [professionals, setProfessionals] = useState<User[]>([]);
  const [professionalCapabilities, setProfessionalCapabilities] = useState<{[professionalId: string]: any[]}>({});
  const [loadingProfessionalCapabilities, setLoadingProfessionalCapabilities] = useState(false);
  const [professionalSchedules, setProfessionalSchedules] = useState<{[professionalId: string]: any[]}>({});
  const [loadingProfessionalSchedules, setLoadingProfessionalSchedules] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [bookingData, setBookingData] = useState<BookingData>({
    selectedServices: [],
    selectedProfessionals: {},
    sameProfessionalForAll: true,
    selectedDate: null,
    selectedTime: '',
    totalPrice: 0,
    totalDuration: 0,
    insistOnProfessional: {},
    notes: undefined,
    serviceAddress: {
      address: '',
      city: '',
      state: '',
      zip: '',
      locationNotes: '',
      locationLabel: ''
    },
    rememberServiceAddress: false,
    quoteRequests: {},
    preferredScheduleText: ''
  });
  const [timeConflict, setTimeConflict] = useState(false);
  const [recurringSettings, setRecurringSettings] = useState<{
    enabled: boolean;
    mode: RecurrenceMode;
    intervalWeeks: number;
    occurrenceCount: number;
  }>({
    enabled: false,
    mode: 'STRICT',
    intervalWeeks: 1,
    occurrenceCount: 10
  });
  const [recurringPreview, setRecurringPreview] = useState<RecurringPreviewResponse | null>(null);
  const [businessPolicies, setBusinessPolicies] = useState<{
    cancelRescheduleUpToValue: number;
    cancelRescheduleUpToUnit: 'hours' | 'days';
    cancellationPolicyText: string;
    depositRequired: boolean;
    depositAmount: number;
    depositNonRefundableOnCancel: boolean;
    depositDueWithinValue: number;
    depositDueWithinUnit: 'minutes' | 'hours' | 'days';
  } | null>(null);
  const [hasAcceptedCancellationPolicy, setHasAcceptedCancellationPolicy] = useState(false);
  const [hasAcceptedDepositLossPolicy, setHasAcceptedDepositLossPolicy] = useState(false);
  const [hasAcceptedDepositProcessingDeadline, setHasAcceptedDepositProcessingDeadline] = useState(false);
  const [customerAppointmentMessage, setCustomerAppointmentMessage] = useState<{ enabled: boolean; message: string } | null>(null);
  const [bookingSlotMinutes, setBookingSlotMinutes] = useState(DEFAULT_BOOKING_SLOT_MINUTES);
  const [phoneCaptureModalOpen, setPhoneCaptureModalOpen] = useState(false);

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
    newlyBookedAppointmentId?: string | number;
    redirectPath?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    newlyBookedAppointmentId: undefined,
    redirectPath: undefined
  });

  // Get pre-selected service from URL params (if coming from "Book Now" button)
  const preselectedServiceId = searchParams.get('serviceId');
  // Get rebook parameters (already parsed above)
  const rebookServiceIds = searchParams.get('serviceIds');
  const rebookProfessionalIds = searchParams.getAll('professionalIds');
  
  // IMPORTANT: `serviceId` should only auto-select once (initial load / param change),
  // and must NOT be re-added after the user de-selects it.
  const hasAppliedPreselectedServiceRef = useRef(false);
  // Track if the user has intentionally removed the preselected service
  const hasRemovedPreselectedServiceRef = useRef(false);
  // Track if rebook data has been applied
  const hasAppliedRebookDataRef = useRef(false);
  // Only refresh user for on-site once to avoid triggering BusinessContext effect loop
  const hasRefreshedUserForOnSiteRef = useRef(false);

  // If the URL param changes, allow one-time auto-selection again for the new serviceId.
  useEffect(() => {
    hasAppliedPreselectedServiceRef.current = false;
    hasRemovedPreselectedServiceRef.current = false;
    hasAppliedRebookDataRef.current = false;
  }, [preselectedServiceId, rebookServiceIds]);

  // If ON_SITE services were removed, or a quotable service is in the selection (no address step), leave address step.
  useEffect(() => {
    const hasOnSiteService = bookingData.selectedServices.some((s: any) => {
      const raw = (s as any).serviceLocation || (s as any).service_location;
      const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
      return loc === 'ON_SITE' || loc === 'ONSITE';
    });
    const hasQuotableService = bookingData.selectedServices.some((s: any) => (s as any)?.quotable === true);
    if (currentStep === 4 && (!hasOnSiteService || hasQuotableService)) {
      setCurrentStep(5);
    }
  }, [bookingData.selectedServices, currentStep]);

  // Signed-in customers on step 1: confirm phone with server (context can lag DB), then show modal only if missing.
  useEffect(() => {
    if (loading || authLoading) return;
    if (!user) {
      setPhoneCaptureModalOpen(false);
      return;
    }
    if (currentStep !== 1) {
      setPhoneCaptureModalOpen(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const fresh = await apiService.getCurrentUser();
        if (cancelled) return;
        updateUser(fresh);
        setPhoneCaptureModalOpen(!userHasPhoneRecord(fresh));
      } catch {
        if (!cancelled) {
          setPhoneCaptureModalOpen(!userHasPhoneRecord(user));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, authLoading, user?.id, currentStep, updateUser]);

  useEffect(() => {
    if (!business?.shortName) return;
    apiService
      .getAppointmentBookingSettings(business.shortName)
      .then((d) => setBookingSlotMinutes(resolveBookingSlotMinutes(d)))
      .catch(() => setBookingSlotMinutes(DEFAULT_BOOKING_SLOT_MINUTES));
  }, [business?.shortName]);

  // Logged-in customers without a phone: prompt on step 1 (public booking only; business dashboard flow unchanged).
  useEffect(() => {
    if (loading || authLoading) return;
    if (!user) {
      setPhoneCaptureModalOpen(false);
      return;
    }
    if (currentStep !== 1) {
      setPhoneCaptureModalOpen(false);
      return;
    }
    if (userHasPhoneRecord(user)) {
      setPhoneCaptureModalOpen(false);
      return;
    }
    setPhoneCaptureModalOpen(true);
  }, [loading, authLoading, user, currentStep]);

  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!businessShortName) return;
      
      try {
        setLoading(true);
        
        // Fetch business details
        // Customer-facing: exclude expired businesses.
        const businessData = await apiService.getBusinessByShortName(businessShortName, { includeExpired: false });
        setBusiness(businessData);

        // Fetch business policies (public endpoint). If missing/empty, booking flow remains unchanged.
        try {
          const policies = await apiService.getBusinessPoliciesByShortName(businessShortName);
          setBusinessPolicies({
            cancelRescheduleUpToValue: policies.cancelRescheduleUpToValue ?? 24,
            cancelRescheduleUpToUnit: policies.cancelRescheduleUpToUnit ?? 'hours',
            cancellationPolicyText: policies.cancellationPolicyText ?? '',
            depositRequired: policies.depositRequired === true,
            depositAmount: Number(policies.depositAmount ?? 0),
            depositNonRefundableOnCancel: policies.depositNonRefundableOnCancel === true,
            depositDueWithinValue: Number(policies.depositDueWithinValue ?? DEFAULT_REGULAR_DEPOSIT_DUE_VALUE),
            depositDueWithinUnit: normalizeDepositDueWithinUnit(policies.depositDueWithinUnit)
          });
        } catch (policyErr) {
          console.warn('Failed to load business policies:', policyErr);
          setBusinessPolicies(null);
        }

        // Fetch customer appointment message (public endpoint). Optional.
        try {
          const msg = await apiService.getCustomerAppointmentMessageByShortName(businessShortName);
          setCustomerAppointmentMessage(msg);
        } catch (msgErr) {
          console.warn('Failed to load customer appointment message:', msgErr);
          setCustomerAppointmentMessage(null);
        }
        
        // Fetch business branding for theme
        const brandingData = await apiService.getBusinessBranding(businessShortName);
        // Convert relative logo URL to full URL if needed
        if (brandingData?.logoUrl && typeof brandingData.logoUrl === 'string') {
          if (!brandingData.logoUrl.startsWith('http://') && !brandingData.logoUrl.startsWith('https://')) {
            const apiBaseUrl = process.env.REACT_APP_API_URL || (process.env.REACT_APP_ENV === 'production' ? 'https://booking-i73o.onrender.com/api' : 'http://localhost:3001/api');
            brandingData.logoUrl = `${apiBaseUrl}${brandingData.logoUrl.startsWith('/') ? brandingData.logoUrl : `/${brandingData.logoUrl}`}`;
          }
        }
        setBranding(brandingData);
        
  // Fetch services WITH categories using businessId (all services, then filter active)
  // The shortName endpoint was not returning populated category objects
  const allServicesWithCategories = await apiService.getAllServices(businessData.id);
  const servicesData = filterCustomerBookableServices(allServicesWithCategories);
  setServices(servicesData);
  
        // Fetch categories
        try {
          const categoriesData = await apiService.getServiceCategories(businessData.id);
          setCategories(categoriesData || []);
        } catch (error) {
          console.error('Error fetching categories:', error);
          setCategories([]);
        }
        
        // Fetch professionals using shortName
        const professionalsData = await apiService.getBusinessProfessionalsByShortName(businessShortName);
        setProfessionals(professionalsData);
        
        // Fetch professional capabilities + schedules (used for bypass + Step 2 display consistency)
        if (professionalsData.length > 0 && businessData.id) {
          fetchProfessionalCapabilities(professionalsData, businessData.id);
          fetchProfessionalSchedules(professionalsData, businessData.id);
        }
        
        // If there's a preselected service (single), add it to booking data
        // Note: Rebook logic is handled in a separate useEffect after services and professionals are loaded
        if (preselectedServiceId && !isRebook) {
          const preselectedService = servicesData.find(s => 
            s.id === preselectedServiceId || s.id.toString() === preselectedServiceId
          );
          if (preselectedService) {
            hasAppliedPreselectedServiceRef.current = true;
            setBookingData(prev => {
              return {
              ...prev,
              selectedServices: [preselectedService],
              selectedProfessionals: { [preselectedService.id]: undefined }, // "Any Professional" by default
              totalPrice: Number((preselectedService as any)?.price ?? 0),
              totalDuration: preselectedService.durationMinutes
              };
            });
          } else {
            console.warn('Preselected service not found! Looking for:', preselectedServiceId);
          }
        }
        
      } catch (error: any) {
        setError(error.message || 'Failed to load business data');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [businessShortName, preselectedServiceId]);

  // If the user saved a preferred on-site address in a prior booking during this session,
  // the auth context may not have it yet. Refresh once when ON_SITE services are selected.
  useEffect(() => {
    const hasOnSiteService = bookingData.selectedServices.some((s: any) => {
      const raw = (s as any).serviceLocation || (s as any).service_location;
      const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
      return loc === 'ON_SITE' || loc === 'ONSITE';
    });
    if (!hasOnSiteService) {
      hasRefreshedUserForOnSiteRef.current = false;
      return;
    }
    if (!user) return;
    if ((user as any).preferredServiceAddress) return;
    if (hasRefreshedUserForOnSiteRef.current) return;

    hasRefreshedUserForOnSiteRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const refreshed = await apiService.getCurrentUser();
        if (!cancelled) updateUser(refreshed);
      } catch {
        hasRefreshedUserForOnSiteRef.current = false; // allow retry on error
      }
    })();

    return () => { cancelled = true; };
  }, [bookingData.selectedServices, user, updateUser]);

  // Fetch professional capabilities
  const fetchProfessionalCapabilities = async (profs: User[], businessId: string) => {
    if (profs.length === 0 || !businessId) return;
    
    try {
      setLoadingProfessionalCapabilities(true);
      const capabilityPromises = profs.map(async (professional) => {
        try {
          const capabilities = await apiService.getProfessionalServices(businessId, professional.id);
          return { professionalId: professional.id, capabilities };
        } catch (error) {
          console.error(`Error fetching capabilities for ${professional.id}:`, error);
          return { professionalId: professional.id, capabilities: [] };
        }
      });
      
      const results = await Promise.all(capabilityPromises);
      const capabilitiesMap: {[professionalId: string]: any[]} = {};
      
      results.forEach(result => {
        capabilitiesMap[result.professionalId] = result.capabilities;
      });
      
      setProfessionalCapabilities(capabilitiesMap);
    } catch (error) {
      console.error('Error fetching professional capabilities:', error);
    } finally {
      setLoadingProfessionalCapabilities(false);
    }
  };

  const fetchProfessionalSchedules = async (profs: User[], businessId: string) => {
    if (profs.length === 0 || !businessId) return;

    try {
      setLoadingProfessionalSchedules(true);
      const schedulePromises = profs.map(async (professional) => {
        try {
          const schedules = await apiService.getProfessionalAvailability(businessId, professional.id);
          return { professionalId: professional.id, schedules };
        } catch (error) {
          console.error(`Error fetching schedule for ${professional.id}:`, error);
          return { professionalId: professional.id, schedules: [] };
        }
      });

      const results = await Promise.all(schedulePromises);
      const schedulesMap: {[professionalId: string]: any[]} = {};
      results.forEach(result => {
        schedulesMap[result.professionalId] = result.schedules || [];
      });
      setProfessionalSchedules(schedulesMap);
    } catch (error) {
      console.error('Error fetching professional schedules:', error);
    } finally {
      setLoadingProfessionalSchedules(false);
    }
  };

  const areCapabilitiesLoaded = () => {
    if (professionals.length === 0) return true;
    return professionals.every(p => Array.isArray(professionalCapabilities[p.id]));
  };

  const areSchedulesLoaded = () => {
    if (professionals.length === 0) return true;
    return professionals.every(p => Array.isArray(professionalSchedules[p.id]));
  };

  const isProfessionalEligible = (professionalId: string) => {
    // Mirror BookingStep2: only exclude on schedule if schedules are loaded.
    if (!areSchedulesLoaded()) return true;
    const schedule = professionalSchedules[professionalId];
    return Array.isArray(schedule) && schedule.length > 0;
  };

  const isProfessionalDataLoaded = () => {
    // We need both capabilities and schedules to make correct bypass/validation decisions
    return areCapabilitiesLoaded() && areSchedulesLoaded();
  };

  const getCapableProfessionalsForService = (serviceId: string): User[] => {
    return professionals.filter(prof => {
      if (prof.canPerformServices === false) return false;
      if (!isProfessionalEligible(prof.id)) return false;
      const capabilities = professionalCapabilities[prof.id];
      if (!Array.isArray(capabilities) || capabilities.length === 0) return false;
      return capabilities.some((capability: any) => {
        const capId = capability.id || capability.serviceId || capability.service_id;
        return String(capId) === String(serviceId);
      });
    });
  };

  const getCapableProfessionalsForAllSelectedServices = (): User[] => {
    if (bookingData.selectedServices.length === 0) return [];
    const sets = bookingData.selectedServices.map(s => new Set(getCapableProfessionalsForService(s.id).map(p => String(p.id))));
    if (sets.length === 0) return [];
    const intersectionIds = Array.from(sets[0]).filter(id => sets.every(set => set.has(id)));
    return professionals.filter(p => intersectionIds.includes(String(p.id)));
  };

  /**
   * Implements the bypass rules:
   * - Bypass ONLY when every selected service has 0 or 1 eligible professionals
   * - Show selection if ANY selected service has 2+ eligible professionals
   *
   * Eligibility already includes: canPerformServices !== false, capability, and (once loaded) having a schedule.
   */
  const shouldBypassProfessionalSelection = (): boolean => {
    if (!isProfessionalDataLoaded()) return false;
    if (bookingData.selectedServices.length === 0) return false;

    return bookingData.selectedServices.every(service => {
      const count = getCapableProfessionalsForService(service.id).length;
      return count <= 1;
    });
  };

  const hasProfessionalChoice = (): boolean => {
    if (!isProfessionalDataLoaded()) return true;
    return !shouldBypassProfessionalSelection();
  };

  /** When the business has exactly one active service-performing professional (typical single-owner setup). */
  const getSoleActiveProfessional = (): User | undefined => {
    if (professionals.length !== 1) return undefined;
    const sole = professionals[0];
    if (sole?.canPerformServices === false) return undefined;
    return sole;
  };

  const formatProfessionalDisplayName = (professional: User): string => {
    if (professional.firstName && professional.lastName) {
      return `${professional.firstName} ${professional.lastName.charAt(0)}.`;
    }
    return professional.email || 'Professional';
  };

  /** Resolve the professional assigned to a service (explicit selection or sole-owner auto-assign). */
  const resolveProfessionalForService = (serviceId: string): User | undefined => {
    const selected = bookingData.selectedProfessionals[serviceId];
    if (selected) return selected;
    if (shouldBypassProfessionalSelection()) {
      const capable = getCapableProfessionalsForService(serviceId);
      if (capable.length === 1) return capable[0];
      if (capable.length === 0) return getSoleActiveProfessional();
    }
    return undefined;
  };

  /** Name shown on date/time step when professional selection is bypassed. */
  const getAutoSelectedProfessionalName = (): string | null => {
    if (bookingData.selectedServices.length === 0) return null;
    if (!shouldBypassProfessionalSelection()) return null;

    for (const service of bookingData.selectedServices) {
      const resolved = resolveProfessionalForService(service.id);
      if (resolved) return formatProfessionalDisplayName(resolved);
    }

    return 'Any Professional';
  };

  // Check if a professional can perform all selected services
  const canPerformAllServices = (professionalId: string): boolean => {
    if (bookingData.selectedServices.length === 0) return true;
    const capabilities = professionalCapabilities[professionalId] || [];
    return bookingData.selectedServices.every(service => {
      return capabilities.some((capability: any) => {
        const capId = capability.id || capability.serviceId || capability.service_id;
        return String(capId) === String(service.id);
      });
    });
  };

  // Ensure rebook data is applied after services and professionals are loaded
  useEffect(() => {
    if (!isRebook || !rebookServiceIds || services.length === 0 || hasAppliedRebookDataRef.current) {
      return;
    }

    const serviceIdArray = rebookServiceIds.split(',').filter(Boolean);
    const preselectedServices = services.filter(s =>
      serviceIdArray.some(id => s.id === id || s.id.toString() === id)
    );

    if (preselectedServices.length === 0) return;

    // Wait for professionals list before resolving URL professional assignments
    const needsProfessionals = rebookProfessionalIds.length > 0;
    if (needsProfessionals && professionals.length === 0) return;

    hasAppliedRebookDataRef.current = true;

    // Parse professional IDs: format is "serviceId:professionalId"
    const professionalMap: { [serviceId: string]: User | undefined } = {};
    preselectedServices.forEach(service => {
      const professionalParam = rebookProfessionalIds.find(param => {
        const [serviceId] = param.split(':');
        return serviceId === service.id || serviceId === service.id.toString();
      });

      if (professionalParam) {
        const [, professionalId] = professionalParam.split(':');
        const professional = professionals.find(p =>
          p.id === professionalId || p.id.toString() === professionalId
        );
        professionalMap[service.id] = professional;
      } else {
        professionalMap[service.id] = undefined; // "Any Professional"
      }
    });

    const totalPrice = preselectedServices.reduce((sum, service) => sum + Number((service as any)?.price ?? 0), 0);
    const totalDuration = preselectedServices.reduce((sum, service) => sum + service.durationMinutes, 0);

    setBookingData(prev => ({
      ...prev,
      selectedServices: preselectedServices,
      selectedProfessionals: professionalMap,
      totalPrice,
      totalDuration
    }));

    // Continue from professional selection (step 2); step=3 URLs are redirected for backward compat
    if (rebookStep === '2' || rebookStep === '3') {
      setCurrentStep(2);
    }
  }, [isRebook, rebookServiceIds, rebookProfessionalIds, rebookStep, services, professionals]);

  // Ensure preselected service is added to selectedServices after services are loaded
  useEffect(() => {
    // Only auto-select once; never re-add after user interaction (e.g., de-selecting to zero).
    // Skip if rebook is active
    if (
      !isRebook &&
      preselectedServiceId &&
      services.length > 0 &&
      bookingData.selectedServices.length === 0 &&
      !hasAppliedPreselectedServiceRef.current
    ) {
      const preselectedService = services.find(s => 
        s.id === preselectedServiceId || s.id.toString() === preselectedServiceId
      );
      if (preselectedService) {
        hasAppliedPreselectedServiceRef.current = true;
        setBookingData(prev => ({
          ...prev,
          selectedServices: [preselectedService],
          selectedProfessionals: { [preselectedService.id]: undefined },
          totalPrice: Number((preselectedService as any)?.price ?? 0),
          totalDuration: preselectedService.durationMinutes
        }));
      }
    }
  }, [preselectedServiceId, services, bookingData.selectedServices.length, isRebook]);

  // Auto-fix impossible "same professional for all" and auto-assign when only one option exists.
  useEffect(() => {
    // Rebook pre-selects professionals from the prior appointment; do not override.
    if (isRebook) return;
    if (!isProfessionalDataLoaded()) return;
    if (bookingData.selectedServices.length === 0) return;

    // If sameProfessionalForAll is ON but nobody can do all selected services (multi-service), force per-service mode.
    if (bookingData.sameProfessionalForAll && bookingData.selectedServices.length > 1) {
      const capableAll = getCapableProfessionalsForAllSelectedServices();
      if (capableAll.length === 0) {
        setBookingData(prev => ({ ...prev, sameProfessionalForAll: false }));
        return;
      }
    }

    const bypass = shouldBypassProfessionalSelection();
    const soleProfessional = getSoleActiveProfessional();

    setBookingData(prev => {
      const nextSelected = { ...prev.selectedProfessionals };
      let changed = false;

      if (prev.sameProfessionalForAll && prev.selectedServices.length > 0) {
        const capableAll = getCapableProfessionalsForAllSelectedServices();
        if (capableAll.length === 1) {
          const assignProfessional = capableAll[0];
          prev.selectedServices.forEach(service => {
            if (nextSelected[service.id]?.id !== assignProfessional.id) {
              nextSelected[service.id] = assignProfessional;
              changed = true;
            }
          });
        }
      }

      prev.selectedServices.forEach(service => {
        const eligible = getCapableProfessionalsForService(service.id);
        if (eligible.length === 1) {
          if (nextSelected[service.id]?.id !== eligible[0].id) {
            nextSelected[service.id] = eligible[0];
            changed = true;
          }
        } else if (bypass && eligible.length === 0) {
          const nextProfessional = soleProfessional ?? undefined;
          if (nextSelected[service.id]?.id !== nextProfessional?.id) {
            nextSelected[service.id] = nextProfessional;
            changed = true;
          }
        }
      });

      if (!changed) return prev;
      return { ...prev, selectedProfessionals: nextSelected };
    });
  }, [
    professionals,
    professionalCapabilities,
    professionalSchedules,
    bookingData.selectedServices,
    bookingData.sameProfessionalForAll,
    isRebook
  ]);

  // If user lands on step 2 but there is no choice, auto-skip to step 3 once capabilities load.
  useEffect(() => {
    if (isRebook) return;
    if (currentStep !== 2) return;
    if (!isProfessionalDataLoaded()) return;

    if (shouldBypassProfessionalSelection()) {
      setCurrentStep(3);
    }
  }, [currentStep, isRebook, professionals, professionalCapabilities, professionalSchedules, bookingData.selectedServices, bookingData.sameProfessionalForAll]);

  const updateBookingData = (updates: Partial<BookingData>) => {
    // Any change to date/time/services/professionals can invalidate a prior recurring preview.
    if (
      updates.selectedDate !== undefined ||
      updates.selectedTime !== undefined ||
      updates.selectedServices !== undefined ||
      updates.selectedProfessionals !== undefined ||
      updates.insistOnProfessional !== undefined
    ) {
      setRecurringPreview(null);
    }

    setBookingData(prev => {
      const updated = { ...prev, ...updates };
      
      // Track if the preselected service was intentionally removed
      if (updates.selectedServices !== undefined && preselectedServiceId) {
        const preselectedWasInPrevious = prev.selectedServices.some(
          s => s.id === preselectedServiceId || s.id.toString() === preselectedServiceId
        );
        const preselectedIsInUpdated = updated.selectedServices.some(
          s => s.id === preselectedServiceId || s.id.toString() === preselectedServiceId
        );
        
        // If preselected service was in previous but not in updated, user removed it
        if (preselectedWasInPrevious && !preselectedIsInUpdated) {
          hasRemovedPreselectedServiceRef.current = true;
        }
      }
      
      // Recalculate totals
      const totalPrice = updated.selectedServices.reduce((sum, service) => sum + Number((service as any)?.price ?? 0), 0);
      const totalDuration = updated.selectedServices.reduce((sum, service) => sum + service.durationMinutes, 0);
      
      // Auto-assign "Any Professional" for new services that don't have a professional selected
      let updatedProfessionals: { [serviceId: string]: User | undefined } = { ...updated.selectedProfessionals };
      if (updates.selectedServices !== undefined) {
        const selectedIds = new Set(updated.selectedServices.map(s => String(s.id)));

        // Remove stale selections for services that were removed.
        Object.keys(updatedProfessionals).forEach((sid) => {
          if (!selectedIds.has(String(sid))) {
            delete updatedProfessionals[sid];
          }
        });

        if (updated.sameProfessionalForAll) {
          // If "same professional" is enabled and a professional is already chosen for any service,
          // ensure ALL selected services inherit that professional (covers newly added services).
          const chosenProfessional =
            updated.selectedServices
              .map(s => updatedProfessionals[s.id])
              .find((p): p is User => Boolean(p && (p as any).id));

          if (chosenProfessional) {
            updated.selectedServices.forEach(service => {
              updatedProfessionals[service.id] = chosenProfessional;
            });
          } else {
            // No professional chosen yet: ensure keys exist with "Any Professional" (undefined).
            updated.selectedServices.forEach(service => {
              if (!Object.prototype.hasOwnProperty.call(updatedProfessionals, service.id)) {
                updatedProfessionals[service.id] = undefined;
              }
            });
          }
        } else {
          // Per-service mode: initialize missing keys to "Any Professional" (undefined).
          updated.selectedServices.forEach(service => {
            if (!Object.prototype.hasOwnProperty.call(updatedProfessionals, service.id)) {
              updatedProfessionals[service.id] = undefined; // "Any Professional"
            }
          });
        }
      }
      
      // Clear time conflict when professionals change
      if (updates.selectedProfessionals !== undefined) {
        setTimeConflict(false);
      }
      
      return { 
        ...updated, 
        selectedProfessionals: updatedProfessionals,
        totalPrice, 
        totalDuration 
      };
    });
  };

  const nextStep = () => {
    const hasOnSiteService = bookingData.selectedServices.some((s: any) => {
      const raw = (s as any).serviceLocation || (s as any).service_location;
      const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
      return loc === 'ON_SITE' || loc === 'ONSITE';
    });

    const shouldHideStep2 =
      !isRebook &&
      bookingData.selectedServices.length > 0 &&
      isProfessionalDataLoaded() &&
      shouldBypassProfessionalSelection();

    const hasQuotableServices = bookingData.selectedServices.some((s: any) => s?.quotable === true);
    // Step ids: 1 = services, 2 = professional, 3 = date/time, 4 = service address (conditional), 5 = quote (when quotable), 6 = review
    // When quotable, skip step 2, 3, and 4 (address not needed; user provides details in quote step).
    const stepsToShow: number[] = shouldHideStep2 ? [1, 3, 4, 5] : [1, 2, 3, 4, 5];
    const stepsWithoutAddress = hasOnSiteService ? stepsToShow : stepsToShow.filter(s => s !== 4);
    const effectiveSteps = hasQuotableServices
      ? [...stepsWithoutAddress.filter((s) => s !== 2 && s !== 3 && s !== 4).slice(0, -1), 5, 6]
      : stepsWithoutAddress;

    const idx = effectiveSteps.indexOf(currentStep);
    if (idx === -1) return;

    // From step 1: when quotable skip to next (5); when non-quotable and bypass, skip to 3
    if (currentStep === 1) {
      if (hasQuotableServices) {
        setCurrentStep(effectiveSteps[1]);
        return;
      }
      if (shouldHideStep2) {
        setCurrentStep(3);
        return;
      }
    }

    const next = effectiveSteps[idx + 1];
    if (typeof next === 'number') setCurrentStep(next);
  };

  const prevStep = () => {
    const hasOnSiteService = bookingData.selectedServices.some((s: any) => {
      const raw = (s as any).serviceLocation || (s as any).service_location;
      const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
      return loc === 'ON_SITE' || loc === 'ONSITE';
    });

    const shouldHideStep2 =
      !isRebook &&
      bookingData.selectedServices.length > 0 &&
      isProfessionalDataLoaded() &&
      shouldBypassProfessionalSelection();

    const hasQuotableServices = bookingData.selectedServices.some((s: any) => s?.quotable === true);
    const stepsToShow: number[] = shouldHideStep2 ? [1, 3, 4, 5] : [1, 2, 3, 4, 5];
    const stepsWithoutAddress = hasOnSiteService ? stepsToShow : stepsToShow.filter(s => s !== 4);
    const effectiveSteps = hasQuotableServices
      ? [...stepsWithoutAddress.filter((s) => s !== 2 && s !== 3 && s !== 4).slice(0, -1), 5, 6]
      : stepsWithoutAddress;

    const idx = effectiveSteps.indexOf(currentStep);
    if (idx <= 0) return;

    const prev = effectiveSteps[idx - 1];
    if (typeof prev === 'number') setCurrentStep(prev);

    // Clear time conflict when going back toward professional selection / date-time
    if (currentStep === 3 || currentStep === 4 || currentStep === 5) {
      setTimeConflict(false);
    }
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        // Require at least 1 non-add-on service before allowing the user to proceed.
        // (If only add-ons are selected, Step 1 is not valid.)
        return bookingData.selectedServices.some((s: any) => {
          const isAddOn = s?.isAddOn ?? s?.is_add_on;
          return isAddOn !== true;
        });
      case 2:
        // Disable Next until capabilities load (prevents \"Next enabled\" while we don't know options yet)
        if (!isProfessionalDataLoaded()) return false;

        // If bypass rules apply, require that any service with exactly 1 eligible professional is assigned to them.
        // (If a service has 0 eligible professionals, it defaults to Any Professional and can proceed.)
        if (shouldBypassProfessionalSelection()) {
          return bookingData.selectedServices.every(service => {
            const resolved = resolveProfessionalForService(service.id);
            const capable = getCapableProfessionalsForService(service.id);
            if (capable.length === 1) {
              return resolved?.id === capable[0].id;
            }
            if (capable.length === 0 && getSoleActiveProfessional()) {
              return resolved?.id === getSoleActiveProfessional()?.id;
            }
            return true;
          });
        }

        // When "Same Professional for all services" is enabled and there are 2+ professionals who can perform all services,
        // require that a professional is actually selected (not undefined/Any Professional)
        if (bookingData.sameProfessionalForAll && bookingData.selectedServices.length > 0) {
          const capableAll = getCapableProfessionalsForAllSelectedServices();
          if (capableAll.length >= 2) {
            // When there are 2+ professionals who can perform all services, require a selection
            const firstService = bookingData.selectedServices[0];
            const selectedProfessional = bookingData.selectedProfessionals[firstService.id];
            if (!selectedProfessional) {
              return false; // No professional selected, cannot proceed
            }
            // Verify the selected professional can actually perform all services
            return canPerformAllServices(selectedProfessional.id);
          }
          if (capableAll.length === 1) {
            const firstService = bookingData.selectedServices[0];
            const selectedProfessional = bookingData.selectedProfessionals[firstService.id];
            return selectedProfessional?.id === capableAll[0].id;
          }
        }

        // For per-service selection mode, require a selection for each service that has 2+ capable professionals.
        // "Any Professional" (undefined) is only allowed when there are 0 or 1 professionals available.
        // If a service has exactly 1 capable professional, that professional must be selected.
        return bookingData.selectedServices.every(service => {
          const capable = getCapableProfessionalsForService(service.id);
          const selectedProfessional = bookingData.selectedProfessionals[service.id];

          // If there's exactly 1 capable professional, require that one to be selected
          if (capable.length === 1) {
            return selectedProfessional?.id === capable[0].id;
          }

          // If there are 2+ capable professionals, require a selection (Any Professional option is hidden in UI)
          if (capable.length >= 2) {
            if (!selectedProfessional) {
              return false; // No professional selected, cannot proceed
            }
            return selectedProfessional.canPerformServices !== false;
          }

          // If there are 0 capable professionals, allow "Any Professional" (undefined)
          return true;
        });
      case 3:
        if (!bookingData.selectedDate || !bookingData.selectedTime || timeConflict) return false;
        if (!recurringSettings.enabled) return true;
        if (!recurringPreview || !Array.isArray(recurringPreview.occurrences)) return false;
        return !recurringPreview.occurrences.some(o => o.status === 'CONFLICT');
      case 4: {
        const hasOnSiteService = bookingData.selectedServices.some((s: any) => {
          const raw = (s as any).serviceLocation || (s as any).service_location;
          const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
          return loc === 'ON_SITE' || loc === 'ONSITE';
        });
        if (!hasOnSiteService) return true;
        const a = bookingData.serviceAddress;
        return !!(a && a.address?.trim() && a.city?.trim() && a.state?.trim() && a.zip?.trim());
      }
      case 5: {
        // Quote step: require single combined details for the quote request
        const quotableSelected = bookingData.selectedServices.filter((s: any) => s?.quotable === true);
        if (quotableSelected.length === 0) return true;
        const details = (bookingData.quoteRequests?.[QUOTE_REQUEST_ALL_KEY]?.details ?? '').trim();
        return details.length > 0;
      }
      default:
        return true;
    }
  };

  const handleCompleteBooking = async () => {
    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      if (!user) {
        setError('You must be logged in to book an appointment');
        setIsSubmitting(false);
        return;
      }

      if (!business) {
        setError('Business information not found');
        setIsSubmitting(false);
        return;
      }

      const hasQuotableServiceForSubmit = bookingData.selectedServices.some((s: any) => s?.quotable === true);
      if (!hasQuotableServiceForSubmit && (!bookingData.selectedDate || !bookingData.selectedTime)) {
        setError('Please select a date and time for your appointment');
        setIsSubmitting(false);
        return;
      }

      // Require acceptance of cancellation policy (custom or default) before submitting.
      if (requiresPolicyAcceptance && !hasAcceptedCancellationPolicy) {
        setError("Please accept the business's cancellation policy to continue.");
        setIsSubmitting(false);
        return;
      }

      if (requiresDepositAcceptance && !hasAcceptedDepositLossPolicy) {
        setError('Please acknowledge the deposit policy to continue.');
        setIsSubmitting(false);
        return;
      }

      if (requiresDepositAcceptance && !hasAcceptedDepositProcessingDeadline) {
        setError(`Please agree to process your deposit within ${formatDepositDueDurationLabel(businessPolicies?.depositDueWithinValue, businessPolicies?.depositDueWithinUnit)} to continue.`);
        setIsSubmitting(false);
        return;
      }

      // Validate that selected professionals can perform services
      const invalidProfessionals = bookingData.selectedServices
        .map(service => bookingData.selectedProfessionals[service.id])
        .filter(professional => professional && professional.canPerformServices === false);

      if (invalidProfessionals.length > 0) {
        setError('One or more selected professionals are not authorized to perform services. Please select different professionals.');
        setIsSubmitting(false);
        return;
      }

      // Create assignments array from selected services and professionals
      const chosenProfessionalId =
        bookingData.sameProfessionalForAll
          ? bookingData.selectedServices
              .map(service => resolveProfessionalForService(service.id)?.id)
              .find((id): id is string => typeof id === 'string' && id.length > 0)
          : undefined;

      const assignments = bookingData.selectedServices.map(service => ({
        serviceId: service.id,
        professionalId: bookingData.sameProfessionalForAll
          ? chosenProfessionalId
          : resolveProfessionalForService(service.id)?.id,
        durationMinutes: service.durationMinutes,
        notes: undefined, // Don't set default notes
        insistOnProfessional: bookingData.insistOnProfessional[service.id] === true
      }));

      const hasOnSiteService = bookingData.selectedServices.some((s: any) => {
        const raw = (s as any).serviceLocation || (s as any).service_location;
        const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
        return loc === 'ON_SITE' || loc === 'ONSITE';
      });

      if (recurringSettings.enabled) {
        // Require a successful, conflict-free preview before submitting recurring
        if (!recurringPreview || !Array.isArray(recurringPreview.occurrences)) {
          setError('Please preview the recurring schedule before submitting.');
          setIsSubmitting(false);
          return;
        }
        if (recurringPreview.occurrences.some(o => o.status === 'CONFLICT')) {
          setError('One or more recurring occurrences have a conflict. Please adjust and preview again.');
          setIsSubmitting(false);
          return;
        }

        const selectedDate = bookingData.selectedDate;
        const selectedTime = bookingData.selectedTime;
        if (!selectedDate || !selectedTime) {
          setError('Please select a date and time for your appointment');
          setIsSubmitting(false);
          return;
        }
        const startDate = formatLocalYmd(selectedDate);
        const startTime = selectedTime;

        const result = await apiService.createRecurringAppointments({
          businessId: business.id,
          customerId: user.id,
          mode: recurringSettings.mode,
          intervalWeeks: recurringSettings.intervalWeeks,
          occurrenceCount: recurringSettings.occurrenceCount,
          startDate,
          startTime,
          clientTimeZone: getBrowserTimeZone(),
          notes: bookingData.notes || undefined,
          ...(hasOnSiteService ? {
            serviceAddress: bookingData.serviceAddress as StepServiceAddress,
            rememberServiceAddress: bookingData.rememberServiceAddress === true
          } : {}),
          assignments
        } as any);

        const depositDueRecurring =
          result.firstAppointmentRequiredDepositAmount != null &&
          Number.isFinite(Number(result.firstAppointmentRequiredDepositAmount)) &&
          Number(result.firstAppointmentRequiredDepositAmount) > 0;

        if (depositDueRecurring && Array.isArray(result.appointmentIds) && result.appointmentIds[0]) {
          navigate(
            `/dashboard/customer/appointments/${encodeURIComponent(String(result.appointmentIds[0]))}/deposit`
          );
          setIsSubmitting(false);
          return;
        }

        const count = Array.isArray(result.appointmentIds) ? result.appointmentIds.length : recurringSettings.occurrenceCount;
        const message =
          result.initialStatus === 'PENDING_QUOTE'
            ? `Your recurring booking and quote request have been received. The business will evaluate your quote request and get back to you as soon as possible. Scheduled ${count} appointment(s).`
            : result.initialStatus === 'CONFIRMED'
              ? `Recurring appointment series booked and confirmed! Scheduled ${count} appointment(s).`
              : `Recurring appointment series booked! Scheduled ${count} appointment(s). Your booking is pending confirmation.`;

        setAlertModal({
          isOpen: true,
          title: 'Success',
          message,
          type: 'success',
          newlyBookedAppointmentId: result.appointmentIds?.[0]
        });
      } else {
        const hasQuotableService = bookingData.selectedServices.some((s: any) => s?.quotable === true);

        if (hasQuotableService) {
          // At least one quotable: upload images to S3 (if any), then create the quote record
          const quotableServices = bookingData.selectedServices.filter((s: any) => s?.quotable === true);
          const preferredSchedule = (bookingData.preferredScheduleText ?? '').trim() || null;
          const quoteRequests = bookingData.quoteRequests || {};
          const allEntry = quoteRequests[QUOTE_REQUEST_ALL_KEY];
          const details = (allEntry?.details ?? '').trim() || '';
          const services = bookingData.selectedServices.map((s: any) => ({
            id: s.id,
            price: s.quotable === true ? null : (s.price != null && Number.isFinite(Number(s.price)) ? Number(s.price) : null)
          }));

          let imageUrls: string[] | undefined;
          const previewUrls = allEntry?.previewUrls ?? [];
          if (previewUrls.length > 0) {
            // Fetch blobs from preview (blob:) URLs, get presigned S3 URLs, upload directly to S3
            const blobs: Blob[] = [];
            const contentTypes: string[] = [];
            for (const url of previewUrls) {
              if (!url?.startsWith('blob:')) continue;
              const res = await fetch(url);
              const blob = await res.blob();
              blobs.push(blob);
              contentTypes.push(blob.type || 'image/jpeg');
            }
            if (blobs.length > 0) {
              const { uploads } = await apiService.getQuotePresignedUploadUrls(blobs.length, contentTypes);
              for (let i = 0; i < blobs.length; i++) {
                const { uploadUrl } = uploads[i];
                const contentType = contentTypes[i] || 'image/jpeg';
                await fetch(uploadUrl, {
                  method: 'PUT',
                  body: blobs[i],
                  headers: { 'Content-Type': contentType }
                });
              }
              imageUrls = uploads.map((u) => u.finalUrl);
            }
          }
          if (!imageUrls?.length) {
            const attachmentIds = Array.isArray(allEntry?.attachmentIds) && allEntry.attachmentIds.length > 0 ? allEntry.attachmentIds : undefined;
            const legacyUrls = Array.isArray(allEntry?.imageUrls) && allEntry.imageUrls.length > 0 ? allEntry.imageUrls : undefined;
            imageUrls = legacyUrls;
            await apiService.createQuote({
              businessId: business.id,
              services,
              preferredSchedule: preferredSchedule ?? undefined,
              customerProvidedDetails: details,
              attachmentIds,
              imageUrls
            });
          } else {
            await apiService.createQuote({
              businessId: business.id,
              services,
              preferredSchedule: preferredSchedule ?? undefined,
              customerProvidedDetails: details,
              imageUrls
            });
          }
          setAlertModal({
            isOpen: true,
            title: 'Success',
            message:
              'Your quote request has been received. The business will evaluate your request and get back to you as soon as possible.',
            type: 'success',
            newlyBookedAppointmentId: undefined,
            redirectPath: '/dashboard/customer/quote-requests'
          });
        } else {
          // At least one non-quotable: create appointment (no quote records from this flow)
          const selectedDate = bookingData.selectedDate;
          const selectedTime = bookingData.selectedTime;
          if (!selectedDate || !selectedTime) {
            setError('Please select a date and time for your appointment');
            setIsSubmitting(false);
            return;
          }
          const appointmentData = {
            businessId: business.id,
            customerId: user.id,
            scheduledAt: new Date(
              selectedDate.getFullYear(),
              selectedDate.getMonth(),
              selectedDate.getDate(),
              parseInt(selectedTime.split(':')[0]),
              parseInt(selectedTime.split(':')[1])
            ).toISOString(),
            notes: bookingData.notes || undefined,
            bookingContext: 'customer' as const,
            ...(hasOnSiteService ? {
              serviceAddress: bookingData.serviceAddress as StepServiceAddress,
              rememberServiceAddress: bookingData.rememberServiceAddress === true
            } : {}),
            assignments: assignments
          };

          const createdAppointment = await apiService.createAppointment(appointmentData);

          const rawDeposit =
            createdAppointment.requiredDepositAmount ??
            (createdAppointment as { required_deposit_amount?: number | string | null }).required_deposit_amount;
          const depositNum = rawDeposit != null ? Number(rawDeposit) : 0;
          const depositDue = Number.isFinite(depositNum) && depositNum > 0;

          if (depositDue && createdAppointment.id) {
            navigate(`/dashboard/customer/appointments/${encodeURIComponent(String(createdAppointment.id))}/deposit`);
            setIsSubmitting(false);
            return;
          }

          let message = '';
          if (createdAppointment.status === 'CONFIRMED') {
            message = 'Appointment booked and confirmed successfully! You will receive a confirmation email shortly.';
          } else {
            message = 'Appointment booked successfully! Your booking is pending confirmation. You will receive an email once it has been confirmed.';
          }

          setAlertModal({
            isOpen: true,
            title: 'Success',
            message,
            type: 'success',
            newlyBookedAppointmentId: createdAppointment.id
          });
        }
      }
      
      // Note: We don't reset isSubmitting here since we're navigating away after user clicks OK
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      setError(error.response?.data?.message || error.message || 'Failed to create appointment');
      setIsSubmitting(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const closeAlertModal = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate(`/${businessShortName}`)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Business
          </button>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">Business Not Found</h2>
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  // Extract colors from branding, with defaults matching the unified layout
  const brandColor = branding?.primaryColor || '#e11d48';
  const brandDark = branding?.secondaryColor || '#9f1239';
  const accentColorValue = branding?.accentColor || '#f5c2cc';
  const brandSoft = `${accentColorValue}15`;
  const bgColor = branding?.backgroundColor || '#ffeef0';
  const surfaceColor = '#ffffff';
  const borderColor = `${accentColorValue}40`;

  // Require policy acceptance for confirmed bookings; quote requests do not require it.
  const hasQuotableServices = bookingData.selectedServices.some((s: any) => (s as any)?.quotable === true);
  const requiresPolicyAcceptance = !hasQuotableServices;
  const requiresDepositAcceptance =
    !hasQuotableServices &&
    businessPolicies?.depositRequired === true &&
    Number(businessPolicies?.depositAmount ?? 0) > 0;
  const textColor = branding?.textColor || '#111827';
  const mutedColor = branding?.textColor ? `${branding.textColor}80` : '#6b7280';

  // Process branding for theme styles (keeping for backward compatibility with booking steps)
  const processedBranding: BusinessBranding = branding || {
    logoUrl: undefined,
    primaryColor: brandColor,
    secondaryColor: brandDark,
    accentColor: accentColorValue,
    textColor: textColor,
    backgroundColor: bgColor,
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    useCustomColors: false,
    layout: 'modern',
    galleryImages: []
  };

  const processedThemeStyles = getThemeStyles(processedBranding);

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: bgColor,
        fontFamily: branding?.fontFamily || 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: textColor
      }}
    >
      {/* Header */}
      {business && (
        <BusinessHeader
          business={business}
          branding={branding || undefined}
          surfaceColor={surfaceColor}
          borderColor={borderColor}
          textColor={textColor}
          mutedColor={mutedColor}
          brandColor={brandColor}
          brandSoft={brandSoft}
          brandDark={brandDark}
          showBookNowButton={false}
        />
      )}

      {/* Booking Header */}
      <header 
        className="border-b"
        style={{
          backgroundColor: surfaceColor,
          borderColor: borderColor
        }}
      >
        <div className="container mx-auto px-4">
          {/* Mobile: Stack vertically */}
          <div className="md:hidden flex flex-col space-y-3 py-4">
            <button 
              onClick={() => navigate(`/${businessShortName}`)}
              className="flex items-center gap-2 font-semibold self-start bg-transparent border-none cursor-pointer p-0"
              style={{ color: brandColor }}
              onMouseEnter={(e) => { e.currentTarget.style.color = brandDark; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = brandColor; }}
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
            <div className="text-center">
              <h1 
                className="text-xl font-semibold" 
                style={{ color: textColor }}
              >
                Book Appointment
              </h1>
              <p 
                className="text-sm mt-1" 
                style={{ color: mutedColor }}
              >
                {(() => {
                  const hasOnSiteService = bookingData.selectedServices.some((s: any) => {
                    const raw = (s as any).serviceLocation || (s as any).service_location;
                    const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
                    return loc === 'ON_SITE' || loc === 'ONSITE';
                  });
                  const hasQuotableServices = bookingData.selectedServices.some((s: any) => s?.quotable === true);
                  const shouldHideStep2 =
                    !isRebook &&
                    bookingData.selectedServices.length > 0 &&
                    isProfessionalDataLoaded() &&
                    shouldBypassProfessionalSelection();
                  const stepsToShow: number[] = shouldHideStep2 ? [1, 3, 4, 5] : [1, 2, 3, 4, 5];
                  const stepsWithoutAddress = hasOnSiteService ? stepsToShow : stepsToShow.filter(s => s !== 4);
                  const effectiveSteps = hasQuotableServices
                    ? [...stepsWithoutAddress.filter((s) => s !== 2 && s !== 3 && s !== 4).slice(0, -1), 5, 6]
                    : stepsWithoutAddress;
                  const totalSteps = effectiveSteps.length;
                  const idx = effectiveSteps.indexOf(currentStep);
                  const displayStep = idx >= 0 ? idx + 1 : 1;
                  return `Step ${displayStep} of ${totalSteps}`;
                })()}
              </p>
            </div>
          </div>
          
          {/* Desktop: Horizontal layout */}
          <div className="hidden md:grid md:grid-cols-[1fr_auto_1fr] md:items-start gap-4 py-4">
            <button 
              onClick={() => navigate(`/${businessShortName}`)}
              className="flex items-center gap-2 font-semibold bg-transparent border-none cursor-pointer p-0 justify-self-start"
              style={{ color: brandColor }}
              onMouseEnter={(e) => { e.currentTarget.style.color = brandDark; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = brandColor; }}
            >
              <ArrowLeft className="w-5 h-5" />
              Back to {business?.name}
            </button>
            <div className="text-center">
              <h1 
                className="text-xl font-semibold leading-tight m-0" 
                style={{ color: textColor }}
              >
                Book Appointment
              </h1>
              <p 
                className="text-sm mt-1 mb-0" 
                style={{ color: mutedColor }}
              >
                {(() => {
                  const hasOnSiteService = bookingData.selectedServices.some((s: any) => {
                    const raw = (s as any).serviceLocation || (s as any).service_location;
                    const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
                    return loc === 'ON_SITE' || loc === 'ONSITE';
                  });
                  const hasQuotableServices = bookingData.selectedServices.some((s: any) => s?.quotable === true);
                  const shouldHideStep2 =
                    !isRebook &&
                    bookingData.selectedServices.length > 0 &&
                    isProfessionalDataLoaded() &&
                    shouldBypassProfessionalSelection();
                  const stepsToShow: number[] = shouldHideStep2 ? [1, 3, 4, 5] : [1, 2, 3, 4, 5];
                  const stepsWithoutAddress = hasOnSiteService ? stepsToShow : stepsToShow.filter(s => s !== 4);
                  const effectiveSteps = hasQuotableServices
                    ? [...stepsWithoutAddress.filter((s) => s !== 2 && s !== 3 && s !== 4).slice(0, -1), 5, 6]
                    : stepsWithoutAddress;
                  const totalSteps = effectiveSteps.length;
                  const idx = effectiveSteps.indexOf(currentStep);
                  const displayStep = idx >= 0 ? idx + 1 : 1;
                  return `Step ${displayStep} of ${totalSteps}`;
                })()}
              </p>
            </div>
            <div aria-hidden="true" />
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div 
        className="border-b" 
        style={{
          backgroundColor: surfaceColor,
          borderColor: borderColor
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center py-4">
            {(() => {
              const hasOnSiteService = bookingData.selectedServices.some((s: any) => {
                const raw = (s as any).serviceLocation || (s as any).service_location;
                const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
                return loc === 'ON_SITE' || loc === 'ONSITE';
              });

              // Check if step 2 should be hidden (only hide when there is no professional choice)
              // But show all steps when rebooking to allow navigation back
              const shouldHideStep2 =
                !isRebook &&
                bookingData.selectedServices.length > 0 &&
                isProfessionalDataLoaded() &&
                shouldBypassProfessionalSelection();
              
              const hasQuotableServices = bookingData.selectedServices.some((s: any) => s?.quotable === true);
              const stepsToShow: number[] = shouldHideStep2 ? [1, 3, 4, 5] : [1, 2, 3, 4, 5];
              const stepsWithoutAddress = hasOnSiteService ? stepsToShow : stepsToShow.filter(s => s !== 4);
              const effectiveSteps = hasQuotableServices
                ? [...stepsWithoutAddress.filter((s) => s !== 2 && s !== 3 && s !== 4).slice(0, -1), 5, 6]
                : stepsWithoutAddress;
              
              return effectiveSteps.map((step, index) => {
                const displayNumber = index + 1;
                const currentIdx = effectiveSteps.indexOf(currentStep);
                const isCompleted = currentIdx >= 0 ? index < currentIdx : step < currentStep;
                const isClickable = isCompleted;
                
                return (
                  <div key={step} className="flex items-center">
                    <div 
                      className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                        isClickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''
                      }`}
                      onClick={isClickable ? () => setCurrentStep(step) : undefined}
                      role={isClickable ? 'button' : undefined}
                      tabIndex={isClickable ? 0 : -1}
                      onKeyDown={isClickable ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setCurrentStep(step);
                        }
                      } : undefined}
                      aria-label={isClickable ? `Go back to step ${displayNumber}` : undefined}
                      title={isClickable ? `Go back to step ${displayNumber}` : undefined}
                      style={step <= currentStep ? {
                        backgroundColor: brandColor,
                        color: '#ffffff',
                        boxShadow: `0 8px 18px ${brandColor}4d`
                      } : {
                        backgroundColor: 'transparent',
                        color: mutedColor,
                        border: `2px solid ${borderColor}`
                      }}
                    >
                      {displayNumber}
                    </div>
                    {index < effectiveSteps.length - 1 && (
                      <div 
                        className="w-16 h-1 mx-2"
                        style={{ 
                          backgroundColor: isCompleted ? brandColor : borderColor
                        }}
                      ></div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1">
        <div className="container mx-auto px-4 py-8">       
        {currentStep === 1 && (
          <BookingStep1
            services={services}
            selectedServices={bookingData.selectedServices}
            onServicesChange={(services: Service[]) => updateBookingData({ selectedServices: services })}
            preselectedServiceId={preselectedServiceId}
            hasRemovedPreselectedService={hasRemovedPreselectedServiceRef.current}
            categories={categories}
            themeStyles={processedThemeStyles}
            isNeonTheme={false}
            neonColors={undefined}
            onNext={nextStep}
            canProceed={bookingData.selectedServices.some((s: any) => {
              const isAddOn = s?.isAddOn ?? s?.is_add_on;
              return isAddOn !== true;
            })}
            businessId={business?.id}
            selectedProfessionals={bookingData.selectedProfessionals}
            selectedDate={bookingData.selectedDate}
            selectedTime={bookingData.selectedTime}
          />
        )}
        
        {currentStep === 2 && (
          <BookingStep2
            selectedServices={bookingData.selectedServices}
            professionals={professionals}
            selectedProfessionals={bookingData.selectedProfessionals}
            onProfessionalsChange={(professionals: { [serviceId: string]: User | undefined }) => updateBookingData({ selectedProfessionals: professionals })}
            professionalCapabilities={professionalCapabilities}
            loadingCapabilities={loadingProfessionalCapabilities}
            sameProfessionalForAll={bookingData.sameProfessionalForAll}
            onSameProfessionalForAllChange={(same: boolean) => updateBookingData({ sameProfessionalForAll: same })}
            themeStyles={processedThemeStyles}
            businessId={business?.id || ''}
            insistOnProfessional={bookingData.insistOnProfessional}
            onInsistOnProfessionalChange={(insistOnProfessional: { [serviceId: string]: boolean }) => updateBookingData({ insistOnProfessional })}
          />
        )}
        
        {currentStep === 3 && (
          <BookingStep3
            selectedDate={bookingData.selectedDate}
            selectedTime={bookingData.selectedTime}
            onDateChange={(date) => updateBookingData({ selectedDate: date })}
            onTimeChange={(time) => updateBookingData({ selectedTime: time })}
            totalDuration={bookingData.totalDuration}
            themeStyles={processedThemeStyles}
            businessId={business?.id || ''}
            customerId={user?.id}
            selectedProfessionals={bookingData.selectedProfessionals}
            selectedServices={bookingData.selectedServices}
            insistOnProfessional={bookingData.insistOnProfessional}
            onTimeConflictChange={setTimeConflict}
            autoSelectFirstAvailableDate={true}
            recurringSettings={recurringSettings}
            onRecurringSettingsChange={(next) => {
              setRecurringPreview(null);
              setRecurringSettings(next);
            }}
            recurringPreview={recurringPreview}
            onRecurringPreviewChange={setRecurringPreview}
            bookingSlotMinutes={bookingSlotMinutes}
            autoSelectedProfessionalName={getAutoSelectedProfessionalName()}
            useBusinessHoursForAvailability={Boolean(getSoleActiveProfessional())}
            soleProfessionalId={getSoleActiveProfessional()?.id}
          />
        )}
        
        {currentStep === 4 && bookingData.selectedServices.some((s: any) => {
          const raw = (s as any).serviceLocation || (s as any).service_location;
          const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
          return loc === 'ON_SITE' || loc === 'ONSITE';
        }) && !bookingData.selectedServices.some((s: any) => (s as any)?.quotable === true) && (
          <BookingStep4ServiceAddress
            serviceAddress={bookingData.serviceAddress as StepServiceAddress}
            onServiceAddressChange={(next) => updateBookingData({ serviceAddress: next })}
            rememberServiceAddress={bookingData.rememberServiceAddress === true}
            onRememberServiceAddressChange={(next) => updateBookingData({ rememberServiceAddress: next })}
            preferredServiceAddress={(user as any)?.preferredServiceAddress || null}
            themeStyles={processedThemeStyles as any}
          />
        )}

        {currentStep === 5 && bookingData.selectedServices.some((s: any) => (s as any)?.quotable === true) && (
          <BookingStepQuote
            quotableServices={bookingData.selectedServices.filter((s: any) => (s as any)?.quotable === true)}
            quoteRequests={bookingData.quoteRequests || {}}
            onQuoteRequestsChange={(next) => updateBookingData({ quoteRequests: next })}
            preferredScheduleText={bookingData.preferredScheduleText ?? ''}
            onPreferredScheduleTextChange={(value) => updateBookingData({ preferredScheduleText: value })}
            businessId={business?.id || ''}
            themeStyles={processedThemeStyles}
          />
        )}

        {((currentStep === 5 && !bookingData.selectedServices.some((s: any) => (s as any)?.quotable === true)) || (currentStep === 6 && !bookingData.selectedServices.some((s: any) => (s as any)?.quotable === true))) && (
          <BookingStep4
            bookingData={{
              ...bookingData,
              insistOnProfessional: bookingData.insistOnProfessional,
              recurringSettings,
              recurringPreview
            }}
            business={business}
            onComplete={handleCompleteBooking}
            themeStyles={processedThemeStyles}
            onNotesChange={(notes) => updateBookingData({ notes })}
            cancellationPolicy={businessPolicies}
            depositRequired={businessPolicies?.depositRequired === true}
            depositAmount={businessPolicies?.depositAmount ?? 0}
            depositNonRefundableOnCancel={businessPolicies?.depositNonRefundableOnCancel === true}
            depositDueWithinValue={businessPolicies?.depositDueWithinValue}
            depositDueWithinUnit={businessPolicies?.depositDueWithinUnit}
            hasAcceptedCancellationPolicy={hasAcceptedCancellationPolicy}
            onAcceptedCancellationPolicyChange={setHasAcceptedCancellationPolicy}
            hasAcceptedDepositLossPolicy={hasAcceptedDepositLossPolicy}
            onAcceptedDepositLossPolicyChange={setHasAcceptedDepositLossPolicy}
            hasAcceptedDepositProcessingDeadline={hasAcceptedDepositProcessingDeadline}
            onAcceptedDepositProcessingDeadlineChange={setHasAcceptedDepositProcessingDeadline}
          />
        )}

        {currentStep === 6 && bookingData.selectedServices.some((s: any) => (s as any)?.quotable === true) && (
          <BookingStepQuoteReview
            business={business!}
            quotableServices={bookingData.selectedServices.filter((s: any) => (s as any)?.quotable === true)}
            nonQuotableServices={bookingData.selectedServices.filter((s: any) => (s as any)?.quotable !== true)}
            quoteRequests={bookingData.quoteRequests || {}}
            preferredScheduleText={bookingData.preferredScheduleText ?? ''}
            selectedProfessionals={bookingData.selectedProfessionals}
            themeStyles={processedThemeStyles}
          />
        )}

        {/* Navigation Buttons */}
        {(() => {
          const hasQuotableServices = bookingData.selectedServices.some((s: any) => (s as any)?.quotable === true);
          const isReviewStep = currentStep === 6 || (currentStep === 5 && !hasQuotableServices);
          return (
        <div className={`flex ${currentStep === 1 ? 'justify-end' : 'justify-between'} gap-3 mt-8`}>
          {currentStep > 1 && (
            <button
              onClick={prevStep}
              className="w-full md:w-auto px-5 py-2.5 text-sm font-medium rounded-lg transition-colors hover:opacity-80 border flex items-center justify-center gap-2"
              style={{
                borderColor: `${brandColor}59`,
                color: brandColor,
                backgroundColor: 'transparent'
              }}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
          )}
          
          {!isReviewStep && (
            <button
              onClick={nextStep}
              disabled={!canProceedToNextStep()}
              className={`w-full md:w-auto px-5 py-2.5 text-sm font-medium rounded-lg transition-opacity flex items-center justify-center gap-2 ${
                !canProceedToNextStep()
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:opacity-90 text-white'
              }`}
              style={!canProceedToNextStep() ? {
                backgroundColor: mutedColor,
                color: textColor
              } : {
                backgroundColor: brandColor,
                borderColor: brandColor,
                boxShadow: `0 8px 18px ${brandColor}4d`
              }}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {isReviewStep && (
            <button
              onClick={handleCompleteBooking}
              disabled={
                isSubmitting ||
                (requiresPolicyAcceptance && !hasAcceptedCancellationPolicy) ||
                (requiresDepositAcceptance && !hasAcceptedDepositLossPolicy) ||
                (requiresDepositAcceptance && !hasAcceptedDepositProcessingDeadline)
              }
              className={`w-full md:w-auto px-5 py-2.5 text-sm font-medium rounded-lg transition-all text-white flex items-center justify-center gap-2 ${
                (
                  isSubmitting ||
                  (requiresPolicyAcceptance && !hasAcceptedCancellationPolicy) ||
                  (requiresDepositAcceptance && !hasAcceptedDepositLossPolicy) ||
                  (requiresDepositAcceptance && !hasAcceptedDepositProcessingDeadline)
                )
                  ? 'cursor-not-allowed' 
                  : 'hover:opacity-90'
              }`}
              style={
                (
                  isSubmitting ||
                  (requiresPolicyAcceptance && !hasAcceptedCancellationPolicy) ||
                  (requiresDepositAcceptance && !hasAcceptedDepositLossPolicy) ||
                  (requiresDepositAcceptance && !hasAcceptedDepositProcessingDeadline)
                )
                  ? {
                backgroundColor: brandColor,
                borderColor: brandColor,
                opacity: 0.7,
                cursor: 'not-allowed'
              } : {
                backgroundColor: brandColor,
                borderColor: brandColor,
                boxShadow: `0 8px 18px ${brandColor}4d`
              }}
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {isSubmitting
                ? (hasQuotableServices ? 'Requesting quote...' : 'Submitting...')
                : (hasQuotableServices ? 'Request Quote' : 'Complete Booking')}
            </button>
          )}
        </div>
          );
        })()}
        </div>
      </main>
      
      {/* Footer */}
      {business && (
        <BusinessFooter 
          business={business}
          branding={branding || undefined}
        />
      )}
      {business && (
        <TenantAskAiWidget
          shortName={business.shortName}
          businessName={business.name}
          enabled={business.askAiEnabled}
          showContactHint={business.showContactForm !== false}
          accentColor={brandColor}
          textColor={textColor}
          placement="booking"
        />
      )}

      {/* Phone capture for signed-in customers missing a number (step 1) */}
      <BookingPhoneRequiredModal
        isOpen={phoneCaptureModalOpen}
        brandColor={brandColor}
        businessName={business.name}
        onSaved={(updatedUser) => {
          updateUser(updatedUser);
          setPhoneCaptureModalOpen(false);
        }}
      />

      {/* Custom Alert Modal */}
      <CustomAlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlertModal}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
        themeColor={brandColor}
        content={
          alertModal.type === 'success' ? (
            <div className="space-y-4">
              <p className="text-gray-700 whitespace-pre-line">{alertModal.message}</p>
              {customerAppointmentMessage?.enabled && (customerAppointmentMessage.message || '').trim() !== '' && (
                <div
                  className="rounded-lg border p-4 flex gap-3"
                  style={{
                    borderColor: `${brandColor}33`,
                    backgroundColor: `${brandColor}0D`,
                  }}
                >
                  <MessageSquare
                    className="flex-shrink-0 mt-0.5 w-5 h-5"
                    style={{ color: brandColor }}
                    aria-hidden
                  />
                  <div className="text-sm text-gray-700 whitespace-pre-line break-words min-w-0">
                    {customerAppointmentMessage.message}
                  </div>
                </div>
              )}
            </div>
          ) : undefined
        }
        onConfirm={() => {
          if (alertModal.redirectPath) {
            navigate(alertModal.redirectPath);
            return;
          }

          const newlyBookedId = alertModal.newlyBookedAppointmentId;
          const redirectUrl = newlyBookedId
            ? `/dashboard/customer/appointments?filter=upcoming&newlyBooked=${newlyBookedId}`
            : '/dashboard/customer/appointments?filter=upcoming';
          navigate(redirectUrl);
        }}
      />
    </div>
  );
};

export default BusinessBookingPage; 